import type { ImportValidationError } from "../types";
import type { MappedRow } from "./mappedRows";

/**
 * Neutralises spreadsheet formula (CSV) injection: a cell whose first character
 * is `= + - @` (or a leading tab / carriage return) is interpreted as a formula
 * by Excel / Google Sheets when the file is opened, which can exfiltrate data or
 * run commands. Prefix such values with an apostrophe so they render as literal
 * text. Applied before quoting.
 *
 * See OWASP "CSV Injection".
 */
function neutraliseFormula(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

/**
 * Wraps a CSV cell value in double quotes if it contains commas, double
 * quotes, or line breaks (`\n` or `\r`) — otherwise returns the value as-is.
 * Formula-trigger characters are neutralised first (CSV injection guard).
 */
function escapeCsvCell(value: string): string {
  const safe = neutraliseFormula(value);
  if (/[",\n\r]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

/** Header row for the mapped output CSV. */
const OUTPUT_HEADER = [
  "Employee Code",
  "Employee Name",
  "Component",
  "CSV Column",
  "Parameter",
  "Bucket Type Id",
  "Value",
];

/**
 * Builds the "output" CSV — the imported data with its complete column→parameter
 * mappings resolved, matching the LADR's desired output. One row per mapped
 * value: each source CSV column is attributed to the Christmas parameter (and
 * bucket type) it writes to, alongside the value.
 *
 * Rows with no mapped values (assignment-only) are still emitted with empty
 * mapping columns so the employee/component assignment is visible.
 */
export function buildOutputCsv(rows: MappedRow[]): string {
  const lines: string[][] = [OUTPUT_HEADER];

  for (const row of rows) {
    if (row.values.length === 0) {
      lines.push([row.employeeCode, row.employeeName, row.component, "", "", "", ""]);
      continue;
    }
    for (const v of row.values) {
      lines.push([
        row.employeeCode,
        row.employeeName,
        row.component,
        v.column,
        v.parameter,
        v.bucketTypeId !== null ? String(v.bucketTypeId) : "",
        v.value,
      ]);
    }
  }

  return lines.map((row) => row.map(escapeCsvCell).join(",")).join("\n");
}

/**
 * A single Christmas composite `paycomponents/setup` request body — one per
 * imported row. Mirrors the LADR schema for
 * `POST /v/3/companies/{companycode}/paycomponents/setup`.
 */
export interface SetupRequestExport {
  componentCode: string;
  /** LADR-038 imports are employee-scoped only (no EmployeeGroup scope). */
  scopeType: "Employee";
  scopeCode: string;
  startDate?: string;
  rowMode: "Create" | "Update";
  parameterValues: { bucketTypeId: number; value: string }[];
}

export interface SetupRequestOptions {
  /** Whether rows create new assignments or update existing ones. */
  rowMode?: "Create" | "Update";
}

/**
 * Builds the Christmas composite request bodies — the "output JSON" we would
 * POST to `paycomponents/setup`, one object per imported row. Parameter values
 * are keyed by `bucketTypeId` (per the LADR, this survives ComponentVersion
 * upgrades); values that could not be resolved to a bucket are omitted.
 */
export function buildSetupRequests(
  rows: MappedRow[],
  options: SetupRequestOptions = {},
): SetupRequestExport[] {
  const { rowMode = "Create" } = options;

  return rows.map((row) => ({
    componentCode: row.component,
    scopeType: "Employee",
    scopeCode: row.employeeCode,
    ...(row.startDate ? { startDate: row.startDate } : {}),
    rowMode,
    parameterValues: row.values
      .filter((v) => v.bucketTypeId !== null)
      .map((v) => ({ bucketTypeId: v.bucketTypeId as number, value: v.value })),
  }));
}

/**
 * Triggers a browser download of *content* as a file, without a server
 * round-trip. The object URL is revoked shortly after the download starts.
 */
function triggerDownload(content: string, mimeType: string, filename: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  // Revoke after a short delay so the browser has time to start the download.
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Triggers a browser download of the mapped output data as a CSV file.
 */
export function downloadOutputCsv(
  rows: MappedRow[],
  filename: string = "pay-component-import-output.csv",
): void {
  triggerDownload(
    buildOutputCsv(rows),
    "text/csv;charset=utf-8;",
    filename,
  );
}

/**
 * Triggers a browser download of the Christmas composite request bodies as a
 * JSON file — the payloads that would be sent to `paycomponents/setup`.
 */
export function downloadOutputJson(
  rows: MappedRow[],
  options: SetupRequestOptions = {},
  filename: string = "pay-component-import-output.json",
): void {
  triggerDownload(
    JSON.stringify(buildSetupRequests(rows, options), null, 2),
    "application/json;charset=utf-8;",
    filename,
  );
}

/**
 * Builds an errors CSV that mirrors the original import file: the exact failed
 * source rows (byte-for-byte), with a trailing `Reason` column appended holding
 * the validation failure(s) for that row (multiple reasons joined by "; ").
 *
 * This keeps the file re-uploadable after fixing — the extra `Reason` column is
 * an unmapped column the import ignores.
 */
export function buildFailedRowsCsv(
  csvText: string,
  errors: ImportValidationError[],
): string {
  const reasonsByRow = new Map<number, string[]>();
  for (const e of errors) {
    const list = reasonsByRow.get(e.row) ?? [];
    list.push(e.error);
    reasonsByRow.set(e.row, list);
  }

  const normalised = csvText.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalised.split("\n");
  if (lines.length === 0 || lines[0].trim() === "") return "";

  const out: string[] = [`${lines[0]},Reason`];
  const dataLines = lines.slice(1).filter((l) => l.trim() !== "");
  dataLines.forEach((line, i) => {
    const reasons = reasonsByRow.get(i + 1);
    if (reasons && reasons.length > 0) {
      out.push(`${line},${escapeCsvCell(reasons.join("; "))}`);
    }
  });
  return out.join("\n");
}

/** Triggers a browser download of arbitrary CSV text (e.g. the failed source rows). */
export function downloadCsvText(text: string, filename: string): void {
  triggerDownload(text, "text/csv;charset=utf-8;", filename);
}
